import { useEffect } from 'react';
import { Collection } from 'src/apis/api-utilities';
import {
  NetworkPoint,
  NetworkPointsSearchParams,
  QuickFilter,
} from 'src/apis/monolith/types';
import { useAxios } from 'src/hooks/use-axios';
import { specialQuickFilters } from 'src/pages/transport/network-points/overview/quick-filter-bar';
import { SearchParams } from 'src/pages/transport/network-points/overview/use-page-params';
import { paramsSerializer } from 'src/utils/axios-instance';
import { useMemoOne } from 'use-memo-one';

export function toCsvUrl(params: NetworkPointsSearchParams) {
  const csvParams: Partial<NetworkPointsSearchParams> = {
    ...params,
    start: undefined,
    pageSize: undefined,
    sortColumn: undefined,
    sortAscending: undefined,
  };

  return `${PRISMA_CONFIG.oldBackendUrl}/networkPoints/csv?${paramsSerializer(
    csvParams
  )}`;
}

export function useApiSearchParams(
  params: SearchParams,
  quickFilters: QuickFilter[]
) {
  return useMemoOne(() => {
    const networkPoints = params.networkPoint.map((np) => np.value);
    const quickFilterNetworkPoints =
      quickFilters
        .find((qf) => qf.name === params.customQuickFilter)
        ?.networkPointList.map((np) => String(np.id)) ?? [];

    // if both quick filter and network point filter is given
    // the intersection of them is used
    let relevantNetworkPoins =
      quickFilterNetworkPoints.length && networkPoints.length
        ? quickFilterNetworkPoints.filter((id) => networkPoints.includes(id))
        : quickFilterNetworkPoints.length
        ? quickFilterNetworkPoints
        : networkPoints;

    // if the intersection of the quick filter and network point filter
    // is empty, we need to filter for a fake non existent NP
    if (
      !relevantNetworkPoins.length &&
      quickFilterNetworkPoints.length &&
      networkPoints.length
    ) {
      relevantNetworkPoins = ['-1'];
    }

    return {
      ...params,
      customQuickFilter: undefined,
      tso: undefined,
      tsoRefId: params.tso.map((s) => s.value),
      networkPoint: relevantNetworkPoins,
      all: params.customQuickFilter === specialQuickFilters.showAll,
      bundled: params.direction.length
        ? params.direction.includes('BUNDLE')
        : undefined,
      ownTsoOnly: params.customQuickFilter === specialQuickFilters.ownTsoOnly,
      showOnlyInactivePoints:
        params.customQuickFilter === specialQuickFilters.inactiveOnly,
    } as NetworkPointsSearchParams;
  }, [params, quickFilters]);
}

export function useNPSearch(params: NetworkPointsSearchParams) {
  const networkPoint = useAxios(
    (axios, signal, params: NetworkPointsSearchParams) =>
      axios.request<Collection<NetworkPoint>>({
        signal,
        url: `${PRISMA_CONFIG.oldBackendUrl}/networkPoints/findNetworkPoints`,
        method: 'GET',
        params,
      }),
    {
      neededOnPageLoad: false,
    }
  );

  useEffect(() => {
    networkPoint.execute(params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  return networkPoint;
}

export type NPSearchRequest = ReturnType<typeof useNPSearch>;
